import apiClient from './client.js';

// Phase 56-02: Parse server-authoritative filename from Content-Disposition.
// Browsers ignore any server-supplied filename when <a download="..."> is set,
// so the frontend must read the header and pass it into a.download.
function filenameFromContentDisposition(header) {
  if (!header) return null;
  const m1 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (m1) {
    try { return decodeURIComponent(m1[1]); } catch { return m1[1]; }
  }
  const m2 = header.match(/filename="([^"]+)"/i);
  if (m2) return m2[1];
  const m3 = header.match(/filename=([^;]+)/i);
  if (m3) return m3[1].trim();
  return null;
}

export const listInvoicesByContract = async (contractId) => {
  const { data } = await apiClient.get(`/contracts/${contractId}/invoices`);
  return data;
};

export const fetchInvoice = async (id) => {
  const { data } = await apiClient.get(`/invoices/${id}`);
  return data;
};

/**
 * Creates a new invoice for the given contract.
 *
 * @param {string} contractId
 * @param {object} payload - Invoice fields.
 * @param {string} payload.issueDate     - ISO 'YYYY-MM-DD'.
 * @param {string} payload.periodStart   - ISO 'YYYY-MM-DD'.
 * @param {string} payload.periodEnd     - ISO 'YYYY-MM-DD'.
 * @param {number|string} payload.fee
 * @param {'paypal'|'payoneer'|'crypto'} [payload.paymentMethod] - Phase 65 D-26 override.
 *   When omitted, backend resolves from contract.paymentMethod (default path, D-27).
 * @param {string} [payload.paymentAddress] - Phase 65 D-26 override.
 *   When omitted, backend resolves from ambassador_data.paymentAddress (default path, D-27).
 *   Both fields must be provided together or both omitted; backend returns 400 on partial.
 */
export const createInvoice = async (contractId, payload) => {
  const { data } = await apiClient.post(`/contracts/${contractId}/invoices`, payload);
  return data;
};

export const downloadInvoicePdf = async (id, fallbackFilename) => {
  const resp = await apiClient.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
  const serverFilename = filenameFromContentDisposition(resp.headers['content-disposition']);
  const finalFilename = serverFilename || fallbackFilename || `invoice-${id}.pdf`;
  const url = URL.createObjectURL(resp.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = finalFilename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const listAllInvoices = async (params = {}) => {
  const { data } = await apiClient.get('/invoices', { params });
  return data;
};

// Phase 57-03: flip manual ops status ('new' | 'in_progress' | 'paid').
// Backend whitelists the value — passing anything else yields 400.
export const updateInvoiceStatus = async (id, status) => {
  const { data } = await apiClient.patch(`/invoices/${id}/status`, { status });
  return data;
};

// Phase 75 D-40: legacy actuality-flip API client REMOVED — backend PATCH /:id/actuality
// endpoint dropped in Plan 75-06 along with the per-row column (75-02). The 5-state FSM
// (draft → in_payment → in_progress → paid / void) collapses the old dimension;
// admin transitions to status='void' via the standard PATCH /:id/status path
// (updateInvoiceStatus below).

// Phase 68 D-01 / D-02: edit invoice period (admin / TL / manager-own).
// Backend regenerates PDF on next /invoices/:id/pdf call — no client-side cache invalidation needed
// beyond TanStack queryKeys.invoices.byContract / .adminList in the consuming component.
// MANAGER → 404 if not own invoice (per-row scope check, identical pattern to status-edit endpoints).
// Backend validates ISO YYYY-MM-DD and periodStart <= periodEnd (400 on either failure).
export const updateInvoicePeriod = async (id, { periodStart, periodEnd }) => {
  const { data } = await apiClient.patch(`/invoices/${id}/period`, { periodStart, periodEnd });
  return data;
};

// Phase 100: partial edit of an invoice. Backend PATCH /invoices/:id accepts
// { bloggerType?, videoCount?, accountId? }. videoCount/accountId require status='draft'
// (server returns 409 otherwise). Pass `null` to clear a field.
export const updateInvoiceFields = async (id, patch) => {
  const { data } = await apiClient.patch(`/invoices/${id}`, patch);
  return data;
};

// P-E1 D6 / P-E2 D6: PATCH /api/invoices/:id/extra-payments — REPLACE-SET semantics. The array is
// the COMPLETE new set of «Дополнительные выплаты»; [] deletes every row. Draft only (LEDGER 322 —
// all invoice roles may edit while status='draft', frozen for everyone after: 409 EXTRAS_FROZEN,
// code readable at err?.data?.code). Backend recomputes invoices.fee = base + Σ and answers with
// { ...invoice, extraPayments, baseFee }.
export const updateInvoiceExtraPayments = async (id, extraPayments) => {
  const { data } = await apiClient.patch(`/invoices/${id}/extra-payments`, { extraPayments });
  return data;
};

// Phase 65 D-14: bulk status PATCH. Returns { ok, updated }.
// Body shape mirrors backend/src/routes/invoices.js bulk-status handler (Plan 65-03).
// `note` is required when downgrading from 'paid' to non-paid (D-16); otherwise optional.
export const bulkUpdateInvoiceStatuses = async ({ invoiceIds, status, note }) => {
  const { data } = await apiClient.patch('/invoices/bulk-status', { invoiceIds, status, note });
  return data;
};

// Phase 65 D-19: GET /invoices/:id/log — DESC-ordered audit rows with
// { id, fromStatus, toStatus, changedAt, note, changedBy: { id, name, email } | null }.
export const fetchInvoiceLog = async (id) => {
  const { data } = await apiClient.get(`/invoices/${id}/log`);
  return data;
};

// Phase 75.1: permanent delete of void invoices (ADMIN/ACCOUNTANT only — backend gate).
// Caller must have already set status='void'; backend returns 409 otherwise.
// FK invoice_status_log.invoice_id has ON DELETE CASCADE so audit rows are wiped too.
export const deleteInvoice = async (id) => {
  const { data } = await apiClient.delete(`/invoices/${id}`);
  return data;
};

// Helper: derive "MMYY" from YYYY-MM filter string (defaults to "all" when absent).
function mmyyFromMonth(month) {
  if (month && /^\d{4}-\d{2}$/.test(month)) {
    return month.slice(5) + month.slice(2, 4);
  }
  return 'all';
}

// Phase 56-04: suffix for download fallback filenames. Mirrors
// backend/src/routes/invoices.js::computeZipSuffix logic client-side so users
// get a meaningful filename even when Content-Disposition is unreadable.
function suffixFromFilters(params = {}) {
  const ISO = /^\d{4}-\d{2}-\d{2}$/;
  const fromOk = params.from && ISO.test(params.from);
  const toOk   = params.to   && ISO.test(params.to);
  if (fromOk) {
    const [y, m, d] = String(params.from).split('-');
    if (toOk && String(params.to).startsWith(`${y}-${m}`)) {
      return m + y.slice(-2);
    }
    const fromCompact = `${y}${m}${d}`;
    return toOk ? `${fromCompact}-${String(params.to).replace(/-/g, '')}` : fromCompact;
  }
  if (toOk) return String(params.to).replace(/-/g, '');
  return mmyyFromMonth(params.month);
}

// Download helper: triggers browser save-as for a blob using a temporary <a>.
function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Admin bulk ZIP — the real PDFs stored in Dropbox (Task 4, 2026-06).
// Returns { skipped } so the caller can toast how many had no Dropbox file.
export const downloadInvoicesZip = async (params = {}) => {
  const resp = await apiClient.get('/invoices/export.zip', { params, responseType: 'blob' });
  const serverFilename = filenameFromContentDisposition(resp.headers['content-disposition']);
  triggerBlobDownload(resp.data, serverFilename || `invoices-${suffixFromFilters(params)}.zip`);
  return { skipped: Number(resp.headers['x-skipped-count'] || 0) };
};

// Admin CSV export — invoice_no, issue_date, period_*, fee, ambassador_*, manager_name.
export const downloadInvoicesCsv = async (params = {}) => {
  const resp = await apiClient.get('/invoices/export.csv', { params, responseType: 'blob' });
  const serverFilename = filenameFromContentDisposition(resp.headers['content-disposition']);
  triggerBlobDownload(resp.data, serverFilename || `invoices-${suffixFromFilters(params)}.csv`);
};

// Компактная выгрузка — bookkeeping CSV (Task 7, 2026-06). Honors active filters.
export const downloadInvoicesCompactCsv = async (params = {}) => {
  const resp = await apiClient.get('/invoices/export-compact.csv', { params, responseType: 'blob' });
  const serverFilename = filenameFromContentDisposition(resp.headers['content-disposition']);
  triggerBlobDownload(resp.data, serverFilename || `invoices-compact-${suffixFromFilters(params)}.csv`);
};

// Batch endpoint — returns { [contractId]: count } map.
// Used by AmbassadorsView to avoid N+1 per-row queries.
//
// Chunked because the GET URL ?contractIds=id1,id2,... exceeds traefik/nginx
// request-line limit (~8KB) when ADMIN loads /documents on a populated DB
// (UUIDs are 37 chars w/ comma, ≈216 fit in 8KB). Without chunking the gateway
// returns no body and the «N инв.» badges silently render as 0 (#phase68 D-29).
// DP: прямая выплата без договора — фиксация суммы+реквизитов, без документа.
export const createDirectPayment = async (leadId, payload) => {
  const { data } = await apiClient.post(`/leads/${leadId}/direct-payments`, payload);
  return data;
};

export const fetchInvoiceCountsByContractIds = async (contractIds) => {
  if (!contractIds || contractIds.length === 0) return {};
  const CHUNK_SIZE = 100;
  const merged = {};
  for (let i = 0; i < contractIds.length; i += CHUNK_SIZE) {
    const chunk = contractIds.slice(i, i + CHUNK_SIZE);
    const { data } = await apiClient.get('/invoices/counts', {
      params: { contractIds: chunk.join(',') },
    });
    Object.assign(merged, data);
  }
  return merged;
};
